/**
 * toolsBindings.js — кнопки меню «Инструменты»: камера, эффекты, CINE,
 * 3D-модель, снимки, отчёты.
 *
 * ПОВОД, и он неприятный. В `index.html` строка 4583 закрывала `<script>`, а
 * следующие 50 строк — семь импортов и около тридцати обработчиков — лежали
 * СЫРЫМ ТЕКСТОМ внутри `<body>`. Браузер такой текст не исполняет, он его
 * показывает: 7616 знаков вываливались на страницу блоком 1920×420 сразу под
 * нижним краем окна, а `body { overflow: hidden }` не давал до них
 * доскроллить. Поэтому никто не замечал — ни глазами, ни аудитом кнопок
 * (все они в свёрнутых панелях и засчитывались как «спрятаны законно»).
 *
 * Итог: 24 кнопки не были привязаны ни к чему. Проверено поиском — их
 * идентификаторы не встречались больше нигде в `src/`.
 *
 * ПОЧЕМУ ТЕПЕРЬ ЭТО ФАЙЛ, А НЕ БЛОК В РАЗМЕТКЕ. Код внутри `index.html`
 * невидим для проверок: `await_test.mjs` разбирает `src/`, `check_imports`
 * разбирает `src/`, ни один тест не читает тело документа. Пока обработчики
 * жили в разметке, такая поломка была принципиально не ловимой. Здесь она
 * ловится.
 *
 * ЧТО ИЗ ТОГО КОДА ВЫЖИЛО И ЧТО НЕТ. При переносе выяснилось, что часть
 * обработчиков и в рабочем виде ничего бы не сделала: они показывали подсказку
 * «задайте центральную линию» и на этом заканчивались, а импортированные
 * setCPR, planStent, measureAneurysmNeck, detectVesselBranches не вызывались
 * ни разу — 22 из 40 импортов блока были мёртвым грузом. Такие кнопки убраны
 * из разметки вместе с обработчиками по правилу, записанному в проекте ещё в
 * 10.3.5: «пустая кнопка в медицинской программе хуже, чем её отсутствие —
 * врач на неё рассчитывает». Что именно убрано и почему — в CHANGELOG_10.19.9.
 */

import { setEdgeEnhancement, isEdgeEnhancementOn, setChromaticAberration, disableChromaticAberration, isChromaticAberrationOn, setVignette, disableVignette, isVignetteOn, setDensityColoring, isDensityColoringOn } from '../core/viewportEffects.js'; // [12.0.51] эффекты — переключатели
import { cameraShake, autoFrameCamera } from '../core/viewportCamera.js'; // [10.19.31] spring/arc ушли вместе с полётами к мировому нулю
import { find3DViewportId, getActiveViewportId, getViewportIds } from '../core/viewportState.js'; // [10.19.31] настоящие окна вместо id кнопок
import { startMultiplanarCine, записатьРоликСерии, getCineState, stopCine } from '../core/viewportCine.js'; // [12.0.57] авто-окно убрано, ролик — проход по серии, 3 плоскости — переключатель
import { окноВыбранное } from './specModes.js';   // [12.0.57] ролик — выбранного окна
import { runMarchingCubes, decimateMesh, smoothMesh, exportSTL, exportGLTF, exportVRML } from '../compute/vtkModule.js';
import { screenshotViewport, screenshotWithFrame, createBeforeAfterComparison, batchExportSlices, exportSliceMetadataCSV } from '../core/viewportScreenshot.js';
import { getVolumeData } from './clinicalTools.js';
import { showToast } from '../utils/helpers.js';
import { getAnnotations } from '../tools/toolManager.js'; // [11.1.18] источник измерений для SR — менеджер, а не window
import { отметитьЭкспорт } from './exportModule.js';      // [11.1.18] экспорты этого файла попадают в «Историю экспорта»

/** Короткая привязка: только если кнопка есть в разметке. */
function on(id, fn) {
  const el = document.getElementById(id);
  if (el) el.addEventListener('click', fn);
  return !!el;
}

/** Скачать blob под именем — один путь для всех экспортов этого файла. */
function скачать(blob, имя) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = имя; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}

const метка = () => new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');

/**
 * [10.19.31] Оба помощника были обманщиками: селектор [data-mode='3d'] первым
 * ловит КНОПКУ верхнего меню (btn-mode-3d — она тоже несёт data-mode), и ядрам
 * уходил id кнопки. Ядро не находило такого окна, кидало исключение, а
 * честная обёртка «сделать» переводила его в «нужно открыть исследование» —
 * даже при открытом. Тринадцать кнопок «Инструментов» вежливо врали годами.
 * Теперь окна настоящие — из реестра, с осмысленным отказом.
 */
function окно3D() {
  const id = find3DViewportId();
  if (!id) throw new Error('нужно объёмное окно — откройте раскладку MPR 6 или режим 3D');
  return id;
}

function окно2D() {
  const ids = getViewportIds() || [];
  if (ids.includes('axial')) return 'axial';
  const акт = getActiveViewportId?.();
  if (акт && ids.includes(акт) && акт !== 'volume3d' && акт !== 'view-3d') return акт;
  const плоское = ids.find((id) => id !== 'volume3d' && id !== 'view-3d');
  if (!плоское) throw new Error('нет открытого окна со срезами — загрузите исследование');
  return плоское;
}

/**
 * Выполнить действие и ОБЯЗАТЕЛЬНО ответить человеку.
 *
 * Зачем обёртка. Браузерная приёмка нашла две кнопки, которые молчали: одна
 * потому, что я не написал ей сообщения, вторая потому, что действие падало
 * без загруженного объёма — исключение съедалось, тост до вызова не доходил.
 * Молчащая кнопка неотличима от непривязанной, а именно с этим весь выпуск и
 * борется. Поэтому: успех — сказать что сделано, отказ — сказать почему.
 *
 * @param {() => any} действие
 * @param {string} успех  что показать, когда получилось
 */
function сделать(действие, успех) {
  try {
    действие();
    if (успех) showToast(успех, 'info', 1500);
  } catch (e) {
    const п = String(e && e.message || e);
    // Самая частая причина здесь — нет объёмного окна (исследование не
    // загружено). Говорим это прямо, а не показываем текст исключения.
    const понятно = /viewport|camera|actor|undefined|null/i.test(п)
      ? 'Не получилось: нужно открыть исследование и объёмное окно'
      : `Не получилось: ${п}`;
    showToast(понятно, 'warning', 4000);
  }
}

// ── Камера ────────────────────────────────────────────────────────────────
// [10.19.31] «Пружина» и «Дуга» УДАЛЕНЫ: обе летели к зашитой мировой точке
// [0,0,−500] с фокусом в нуле — мимо тела пациента, родня похороненного
// «Облёта по кривой». Живое движение камеры честно есть рядом: «Авто-фрейм»
// (кадр по объёму), «Тряска» (относительна текущему виду), «Автовращение»
// (спец-режимы, 10.19.30) и Гориз./Верт. в левом сайдбаре.
on('btn-cam-shake', () => сделать(
  () => cameraShake(окно3D(), { intensity: 5, duration: 500 }),
  'Встряска камеры'));
on('btn-auto-frame', () => сделать(
  () => autoFrameCamera(окно3D()),
  'Кадр подобран по объёму'));

// ── Эффекты объёмного окна ────────────────────────────────────────────────
// [12.0.51] «Туман» (btn-depth-cueing) УДАЛЁН: vtk.js не умеет затемнять дальнее
// у объёмного маппера (метода нет), кнопка ничего не делала и печатала
// «Дальние структуры притемнены». Настоящий туман — в кинорендере выше.
//
// [12.0.51] Остальные четыре — ПЕРЕКЛЮЧАТЕЛИ. Раньше каждое нажатие только
// включало: снять виньетку, аберрацию, подчёркнутые края или цветовую шкалу
// было нечем, кроме смены пресета или перезагрузки. Теперь второе нажатие
// выключает и возвращает как было; кнопка подсвечена, пока эффект включён.
// Состояние читается у самого эффекта (is…On), а не у кнопки: окно могло
// смениться, и кнопка не должна врать.
function переключатель(ид, { вкл, выкл, включено, сказатьВкл, сказатьВыкл }) {
  on(ид, () => {
    const кнопка = document.getElementById(ид);
    let окно;
    try { окно = окно3D(); } catch (e) { showToast(`Не получилось: ${e.message}`, 'warning', 4000); return; }
    try {
      if (включено(окно)) {
        выкл(окно);
        кнопка?.classList.remove('active');
        showToast(сказатьВыкл, 'info', 1500);
        return;
      }
      const р = вкл(окно);
      const ок = р === true || р?.ok === true;
      if (!ок) { showToast(`Не получилось: ${р?.причина || 'объёмное окно не приняло эффект'}`, 'warning', 4500); return; }
      кнопка?.classList.add('active');
      showToast(сказатьВкл, 'info', 1500);
    } catch (e) {
      showToast(`Не получилось: ${e.message}`, 'warning', 4000);
    }
  });
}

переключатель('btn-edge-enhance', {
  вкл: (о) => setEdgeEnhancement(о, true), выкл: (о) => setEdgeEnhancement(о, false), включено: isEdgeEnhancementOn,
  сказатьВкл: 'Края подчёркнуты: однородное вещество стало прозрачнее, границы тканей — ярче', сказатьВыкл: 'Края: как было',
});
переключатель('btn-chromatic-ab', {
  вкл: (о) => setChromaticAberration(о, 2), выкл: disableChromaticAberration, включено: isChromaticAberrationOn,
  сказатьВкл: 'Хроматическая аберрация включена (только картинка на экране)', сказатьВыкл: 'Хроматическая аберрация выключена',
});
переключатель('btn-vignette', {
  вкл: (о) => setVignette(о, 0.5), выкл: disableVignette, включено: isVignetteOn,
  сказатьВкл: 'Виньетка включена', сказатьВыкл: 'Виньетка выключена',
});
переключатель('btn-thickness-color', {
  вкл: (о) => setDensityColoring(о, true), выкл: (о) => setDensityColoring(о, false), включено: isDensityColoringOn,
  сказатьВкл: 'Цвет по плотности: воздух синий, мягкие ткани зелёные, кость оранжево-красная, металл белый',
  сказатьВыкл: 'Цвета объёма — как были',
});

// ── CINE ──────────────────────────────────────────────────────────────────
// [12.0.57] «Авто-окно» (btn-cine-autowin) УДАЛЕНО — надгробие в index.html:
// тост «Авто-окно при прокрутке включено» при любом исходе, флажок ложился
// только в уже идущее кино, а кино читало свой флажок, заданный при запуске.
// И сама затея вредна: окно, само ползущее по яркости центра кадра, красит
// одну плотность на разных срезах по-разному.

// [12.0.57] Ролик — ВЫБРАННОГО окна и всей серии (core/viewportCine.
// записатьРоликСерии). Прежде: всегда окно 'axial' (выбранное — мимо), три
// секунды с того места, куда прыгнет кино (у реформата — с нулевого среза), и
// окно оставалось на чужом срезе.
let _роликИдёт = false;
on('btn-cine-export-mp4', async () => {
  if (_роликИдёт) { showToast('Ролик уже пишется — дождитесь конца', 'info', 2000); return; }
  const о = окноВыбранное({ плоское: true });
  if (!о.ok) { showToast(`Ролик: ${о.причина}`, 'warning', 4000); return; }
  _роликИдёт = true;
  showToast(`Записываю ролик окна «${о.id}»: проход по всей серии…`, 'info', 2500);
  try {
    const р = await записатьРоликСерии(о.id, { кадровВСек: 15, пределСек: 30 });
    const расш = р.тип === 'video/mp4' ? 'mp4' : 'webm';
    скачать(р.blob, `cine_${метка()}.${расш}`);
    showToast(`Ролик сохранён: ${р.кадров} кадров${р.шаг > 1 ? ` (каждый ${р.шаг}-й из ${р.всего})` : ` из ${р.всего}`}, ${р.секунд.toFixed(1)} с, ${(р.blob.size / 1048576).toFixed(1)} МБ, ${расш}. Срез вернулся на место`, 'success', 4500);
  } catch (e) {
    showToast(`Ролик не записался: ${e.message}`, 'error', 5000);
  } finally {
    _роликИдёт = false;
  }
});

// [12.0.57] «3-плоскости» — ПЕРЕКЛЮЧАТЕЛЬ. Прежде второе нажатие запускало
// кино заново, а остановить его было нечем, кроме нижней панели кино;
// реформаты начинали с нулевого среза (поправлено в viewportCine.startCine).
on('btn-cine-multiplanar', () => сделать(() => {
  // [10.19.31] Здесь стояли окна-призраки 'view-axial/sagittal/coronal' —
  // таких id нет ни в одной раскладке, «3-плоскости» молча не крутили ничего.
  const кнопка = document.getElementById('btn-cine-multiplanar');
  const есть = new Set(getViewportIds() || []);
  const оси = ['axial', 'sagittal', 'coronal'].filter((id) => есть.has(id));
  if (оси.length < 2) throw new Error('нужна раскладка MPR (клавиша Q) — три плоскости рядом');
  if (getCineState(оси[0])?.playing) {
    stopCine(оси[0]);
    кнопка?.classList.remove('active');
    showToast('Листание в трёх плоскостях остановлено', 'info', 1500);
    return;
  }
  startMultiplanarCine(оси);
  if (!getCineState(оси[0])?.playing) throw new Error('кино не запустилось — в окне один срез');
  кнопка?.classList.add('active');
  showToast('Листание во всех трёх плоскостях — повторное нажатие остановит', 'info', 2000);
}, ''));

// ── Трёхмерная модель ─────────────────────────────────────────────────────
//
// Все четыре кнопки раньше только показывали подсказку «загрузите 3D модель»,
// хотя загружать нечего: сетки в программе нет как состояния. Теперь сетка
// СТРОИТСЯ из активного тома тем же marching cubes, что и для STL, и сразу
// уходит в файл. Порог 150 HU — кость на КТ; для МРТ он бессмыслен, поэтому
// при неудаче говорим об этом прямо, а не молчим.

const ПОРОГ_КОСТИ_HU = 150;

/** Построить сетку из активного тома. Возвращает {mesh, треугольников} или null. */
// [11.1.21] КЭШ ПОСЛЕДНЕЙ СЕТКИ (аудит §3.3). Раньше каждая кнопка ряда —
// Децимация, Сгладить, GLTF, VRML, STL — запускала СВОЙ marching cubes с
// нуля: пользовательская цепочка «построил → упростил → выгрузил» на деле
// была «посчитал → посчитал ещё раз → и ещё». Ключ кэша — сам массив
// вокселей: загрузили другой том — массив другой, кэш сам умер.
let _кэшСетки = { данные: null, mesh: null, треугольников: 0, источник: '' };

/**
 * [11.1.22] Принять сетку, построенную СНАРУЖИ, — сведение двух миров по
 * слову владельца: «Построить поверхность» из вкладки GPU кладёт сюда свой
 * результат (уже сконвертированный в points/polys и мм — compute/
 * meshConvert), и весь ряд «Децимация/Сгладить/GLTF/VRML/STL» берёт его без
 * пересчёта. Ключ тот же — сам массив вокселей: другой том — кэш умирает.
 */
export function положитьСетку(данныеКлюч, mesh, треугольников, источник = '') {
  if (!данныеКлюч || !mesh?.points?.length) return false;
  _кэшСетки = { данные: данныеКлюч, mesh, треугольников: треугольников || 0, источник };
  return true;
}

async function построитьСетку() {
  const v = getVolumeData();
  if (!v?.scalarData || !v?.dims) {
    showToast('Сначала загрузите исследование — сетку строить не из чего', 'warning', 4000);
    return null;
  }
  if (_кэшСетки.данные === v.scalarData && _кэшСетки.mesh) {
    const откуда = _кэшСетки.источник ? ` — ${_кэшСетки.источник}` : '';
    showToast(`Беру уже построенную поверхность (${_кэшСетки.треугольников.toLocaleString('ru-RU')} треуг.${откуда}) — пересчёта не будет`, 'info', 2500);
    return { mesh: _кэшСетки.mesh, треугольников: _кэшСетки.треугольников };
  }
  showToast('Готовой поверхности нет — строю по порогу 150 HU (займёт время)…', 'info', 3000);
  const mesh = await runMarchingCubes(v.scalarData, v.dims, v.spacing || [1, 1, 1], ПОРОГ_КОСТИ_HU);
  if (!mesh?.points?.length) {
    showToast('Поверхность не построилась: при пороге 150 HU нечего выделять (для МРТ это ожидаемо)', 'warning', 6000);
    return null;
  }
  const треугольников = Math.round((mesh.polys?.length || 0) / 4);
  _кэшСетки = { данные: v.scalarData, mesh, треугольников, источник: 'построена здесь, порог 150 HU' };
  return { mesh, треугольников };
}

on('btn-mesh-decimate', async () => {
  const s = await построитьСетку(); if (!s) return;
  const меньше = await decimateMesh(s.mesh, 0.5);
  const было = s.треугольников;
  const стало = Math.round((меньше?.polys?.length || 0) / 4) || было;
  const r = await exportSTL(меньше || s.mesh, `model_decimated_${метка()}.stl`);
  showToast(r?.success
    ? `STL упрощён: ${было} → ${стало} треугольников, ${r.sizeKb} КБ`
    : 'Упростить удалось, а записать файл — нет', r?.success ? 'success' : 'error', 5000);
});

on('btn-mesh-smooth', async () => {
  const s = await построитьСетку(); if (!s) return;
  const гладкая = await smoothMesh(s.mesh, 20, 0.1);
  const r = await exportSTL(гладкая || s.mesh, `model_smoothed_${метка()}.stl`);
  showToast(r?.success
    ? `STL сглажен (20 проходов), ${s.треугольников} треугольников, ${r.sizeKb} КБ`
    : 'Сгладить удалось, а записать файл — нет', r?.success ? 'success' : 'error', 5000);
});

// [10.19.10] STL — единственный обработчик. Раньше их было два (appInit звал
// exportSTL() без аргументов и проверял несуществующий result.blob;
// featureBindings показывал подсказку), и оба не работали.
on('btn-export-stl', async () => {
  const s = await построитьСетку(); if (!s) return;
  const r = await exportSTL(s.mesh, `model_${метка()}.stl`);
  if (r?.success) отметитьЭкспорт('STL', `model_${метка()}.stl`);
  showToast(r?.success
    ? `STL сохранён: ${s.треугольников} треугольников, ${r.sizeKb} КБ`
    : 'STL записать не удалось', r?.success ? 'success' : 'error', 5000);
});

on('btn-export-gltf', async () => {
  const s = await построитьСетку(); if (!s) return;
  const r = await exportGLTF(s.mesh, `model_${метка()}.gltf`, false);
  showToast(r?.success !== false
    ? `glTF сохранён, ${s.треугольников} треугольников`
    : 'glTF записать не удалось', r?.success !== false ? 'success' : 'error', 4000);
});

on('btn-export-vrml', async () => {
  const s = await построитьСетку(); if (!s) return;
  const r = exportVRML(s.mesh, `model_${метка()}.wrl`);
  showToast(r?.success !== false
    ? `VRML сохранён, ${s.треугольников} треугольников`
    : 'VRML записать не удалось', r?.success !== false ? 'success' : 'error', 4000);
});

// ── Снимки ────────────────────────────────────────────────────────────────
on('btn-screenshot-frame', async () => {
  const dataUrl = await screenshotWithFrame(окно2D(), { title: 'Mikhmel Cornerstone4D' });
  if (!dataUrl) { showToast('Снимок не получился: окно пустое', 'warning', 3000); return; }
  const a = document.createElement('a');
  a.href = dataUrl; a.download = `snapshot_${метка()}.png`; a.click();
  отметитьЭкспорт('снимок с рамкой', a.download);
  showToast('Снимок с рамкой сохранён', 'success', 2000);
});

// Сравнение «до/после» в два нажатия. Раньше кнопка показывала подсказку
// «сделайте первый скриншот», но запомнить его было некуда — состояния не
// существовало. Теперь первое нажатие запоминает, второе собирает картинку.
let _снимокДо = null;
on('btn-compare-before-after', async () => {
  const vp = окно2D();
  if (!_снимокДо) {
    _снимокДо = await screenshotViewport(vp, { overlay: false });
    if (!_снимокДо) { showToast('Не удалось снять «до»: окно пустое', 'warning', 3000); return; }
    showToast('Снято «ДО». Измените что-нибудь и нажмите ещё раз', 'info', 4000);
    return;
  }
  const dataUrl = await createBeforeAfterComparison(vp, _снимокДо, {});
  _снимокДо = null;
  if (!dataUrl) { showToast('Сравнение не собралось', 'warning', 3000); return; }
  const a = document.createElement('a');
  a.href = dataUrl; a.download = `before_after_${метка()}.png`; a.click();
  отметитьЭкспорт('до/после', a.download);
  showToast('Сравнение «до/после» сохранено', 'success', 2500);
});

on('btn-batch-zip', async () => {
  showToast('Упаковываю каждый пятый срез в ZIP…', 'info', 3000);
  try {
    await batchExportSlices(окно2D(), { zip: true, step: 5 });
    отметитьЭкспорт('ZIP срезов', '');
  } catch (e) {
    showToast(`Упаковать не удалось: ${e.message}`, 'error', 5000);
  }
});

// ── Отчёты ────────────────────────────────────────────────────────────────
on('btn-export-csv', async () => {
  const csv = await exportSliceMetadataCSV(окно2D());
  if (!csv) { showToast('Метаданные срезов не собрались', 'warning', 3000); return; }
  отметитьЭкспорт('CSV метаданных', `slices_${метка()}.csv`);
  скачать(new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' }), `slices_${метка()}.csv`);
  showToast('Таблица срезов сохранена (CSV)', 'success', 2500);
});

// btn-dicom-sr раньше звал exportDICOMSR из viewportPersistence — а тот отдаёт
// JSON, файл при этом назывался «sr_*.json», а тост говорил «DICOM SR
// экспортирован». Это была неправда: настоящий SR (Comprehensive 3D SR,
// бинарный .dcm, под тестами sr_test/srcoord_test) живёт в io/dicomSRModule.js.
// Теперь кнопка зовёт настоящий путь — тот же, что и «Отчёты → DICOM SR».
on('btn-dicom-sr', async () => {
  try {
    // [11.1.18] Раньше измерения искались на window.cornerstoneTools /
    // MikhmelViewer._csTools — ни того, ни другого программа не выставляет,
    // и SR всегда отвечал «нет измерений», хотя линейный замер стоял на
    // экране (третье замечание владельца). Источник — toolManager, тот же,
    // из которого таблицу берёт PDF-отчёт.
    const annotations = getAnnotations() ||
      window.cornerstoneTools?.annotation?.state?.getAllAnnotations?.() || [];
    if (!annotations.length) {
      showToast('Нет измерений для отчёта — сделайте хотя бы одно', 'warning', 4000);
      return;
    }
    const серии = window.MikhmelViewer?.getAllSeries?.() || [];
    const s = серии[0] || {};
    const { exportAnnotationsToDicomSR, downloadDicomSR } = await import('../io/dicomSRModule.js');
    const blob = await exportAnnotationsToDicomSR(annotations, {
      patientName: s.patientName || 'ANONYMOUS',
      patientId: s.patientId || '',
      studyUID: s.studyUID || undefined,
      modality: s.modality || 'CT',
      observerName: 'Mikhmel A.V.',
    });
    downloadDicomSR(blob, `SR_${метка()}.dcm`);
    отметитьЭкспорт('DICOM SR', `SR_${метка()}.dcm`, blob.size / 1048576);
    showToast(`DICOM SR сохранён: ${annotations.length} измерений, ${(blob.size / 1024).toFixed(1)} КБ`, 'success', 3000);
  } catch (e) {
    showToast(`DICOM SR не сохранился: ${e.message}`, 'error', 5000);
  }
});

export default { on };
